import type { SupplierSortField } from '../types/supplier';

const STORAGE_KEY = 'brix.suppliersTable';

export type SortDir = 'asc' | 'desc';

export const SUPPLIER_SORT_FIELDS: readonly SupplierSortField[] = [
  'display_name',
  'email',
  'payment_terms',
  'balance',
  'active',
  'created_at',
] as const;

const SUPPLIER_SORT_FIELD_SET = new Set<string>(SUPPLIER_SORT_FIELDS);

export type SuppliersTablePreferences = {
  sort: SupplierSortField | `-${SupplierSortField}`;
  perPage: number;
};

const DEFAULT_PREFERENCES: SuppliersTablePreferences = {
  sort: 'display_name',
  perPage: 25,
};

export const encodeSupplierSort = (
  field: SupplierSortField,
  direction: SortDir
): SuppliersTablePreferences['sort'] => (direction === 'desc' ? `-${field}` : field);

export const parseSupplierSort = (
  value: string
): { field: SupplierSortField; direction: SortDir } | null => {
  const direction: SortDir = value.startsWith('-') ? 'desc' : 'asc';
  const field = (value.startsWith('-') ? value.slice(1) : value) as SupplierSortField;

  if (!SUPPLIER_SORT_FIELD_SET.has(field)) {
    return null;
  }

  return { field, direction };
};

export const loadSuppliersTablePreferences = (): SuppliersTablePreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<SuppliersTablePreferences>;
    const sort =
      typeof parsed.sort === 'string' && parseSupplierSort(parsed.sort)
        ? (parsed.sort as SuppliersTablePreferences['sort'])
        : DEFAULT_PREFERENCES.sort;
    const perPage =
      typeof parsed.perPage === 'number' && parsed.perPage >= 1 && parsed.perPage <= 100
        ? parsed.perPage
        : DEFAULT_PREFERENCES.perPage;

    return { sort, perPage };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const saveSuppliersTablePreferences = (prefs: SuppliersTablePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / private browsing errors.
  }
};
